// src/cn/pages/TrainingResponse/TrainingResponsePageCN.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TrainingResponseAI } from "../../../lib/trainingresponseApi";

function LoaderDot() {
  return <span className="loader-dot" />;
}

export default function TrainingResponsePageCN() {
  const [chat, setChat] = useState([]);
  const [input, setInput] = useState("");
  const [desc, setDesc] = useState("");

  const [customText, setCustomText] = useState("");
  const [scenarioBusy, setScenarioBusy] = useState(false);

  const [busy, setBusy] = useState(false);
  const [emotionCard, setEmotionCard] = useState(null);
  const [strategyText, setStrategyText] = useState("");
  const [predicted, setPredicted] = useState([]);
  const [modelAns, setModelAns] = useState(null);
  const [summary, setSummary] = useState(null);
  const [report, setReport] = useState(null);

  const didInit = useRef(false);

  const lang = (typeof document !== "undefined" && document.documentElement?.lang) || "ko";

  // 초기 시나리오
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    (async () => {
      try {
        const init = await TrainingResponseAI.init();
        setChat([{ role: "ai", text: init.aiMessage.parts?.[0]?.text || "" }]);
        setDesc(init.description || "");
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const hasAtLeastOneUserMsg = useMemo(() => chat.some((m) => m.role === "user"), [chat]);

  const resetAllViews = useCallback(() => {
    setEmotionCard(null);
    setStrategyText("");
    setPredicted([]);
    setModelAns(null);
    setSummary(null);
    setReport(null);
  }, []);

  const handleReset = useCallback(async () => {
    resetAllViews();
    setCustomText("");
    setInput("");
    try {
      const init = await TrainingResponseAI.init();
      setChat([{ role: "ai", text: init.aiMessage.parts?.[0]?.text || "" }]);
      setDesc(init.description || "");
    } catch (e) {
      console.error(e);
    }
  }, [resetAllViews]);

  const handleStartCustomScenario = useCallback(async () => {
    const t = customText.trim();
    if (!t) {
      alert("请输入场景。");
      return;
    }
    setScenarioBusy(true);
    try {
      const sc = await TrainingResponseAI.customScenario(t);
      resetAllViews();
      setChat([{ role: "ai", text: sc.aiMessage.parts?.[0]?.text || "" }]);
      setDesc(sc.description || "");
      setInput("");
    } catch (e) {
      console.error(e);
      alert("场景生成失败。请重试。");
    } finally {
      setScenarioBusy(false);
    }
  }, [customText, resetAllViews]);

  const handleSend = useCallback(async () => {
    const t = input.trim();
    if (!t || busy) return;

    resetAllViews();
    setBusy(true);
    try {
      const nextChat = [...chat, { role: "user", text: t }];
      setChat(nextChat);

      // 감정 분석 + 코칭
      try {
        const emo = await TrainingResponseAI.emotionCoaching(t);
        setEmotionCard(emo);
      } catch (e) {
        console.error("emotionCoaching", e);
      }

      // 다음 응답 + 전략
      try {
        const { response, strategy } = await TrainingResponseAI.reply(nextChat);
        setChat((prev) => [...prev, { role: "ai", text: response }]);
        setStrategyText(strategy || "");
      } catch (e) {
        console.error("reply", e);
        setChat((prev) => [...prev, { role: "ai", text: "抱歉，系统发生错误。请稍后再试。" }]);
      }
    } finally {
      setBusy(false);
      setInput("");
    }
  }, [input, chat, busy, resetAllViews]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handlePredict = useCallback(async () => {
    if (!hasAtLeastOneUserMsg) return;
    try {
      const firstUser = chat.find((m) => m.role === "user")?.text || "";
      const arr = await TrainingResponseAI.predictQuestions(firstUser);
      setPredicted(Array.isArray(arr) ? arr : []);
    } catch (e) {
      console.error(e);
      setPredicted([]);
      alert("获取预测问题失败。");
    }
  }, [chat, hasAtLeastOneUserMsg]);

  const handleModelAnswer = useCallback(async () => {
    if (!hasAtLeastOneUserMsg) return;
    try {
      const r = await TrainingResponseAI.modelAnswer(chat);
      setModelAns(r);
    } catch (e) {
      console.error(e);
      setModelAns(null);
      alert("生成示范回答失败。");
    }
  }, [chat, hasAtLeastOneUserMsg]);

  const handleSummarize = useCallback(async () => {
    if (!hasAtLeastOneUserMsg) return;
    try {
      const r = await TrainingResponseAI.summarize(chat);
      setSummary(r);
    } catch (e) {
      console.error(e);
      setSummary(null);
      alert("对话分析失败。");
    }
  }, [chat, hasAtLeastOneUserMsg]);

  const handleReport = useCallback(async () => {
    if (!hasAtLeastOneUserMsg) return;
    try {
      const r = await TrainingResponseAI.report(chat);
      setReport(r);
    } catch (e) {
      console.error(e);
      setReport(null);
      alert("生成报告失败。");
    }
  }, [chat, hasAtLeastOneUserMsg]);

  return (
    <section>
      <div id="content_title">
        <h2>AI 投诉顾客应对训练（反向）</h2>
        <p>现在由您扮演投诉顾客，训练 AI 员工的应对。</p>
      </div>

      <div className="content">
        {/* 1) 시나리오 선택 */}
        <div className="mrdef">
          <h3 className="sectionTitle">1. 选择场景</h3>
          <div>
            <p>可使用默认场景开始，或输入专属场景。</p>
            <div className="chatInput mrdef">
              <input
                placeholder="例如：我等候时间比预约时间久太多了。"
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
              />
              <button className="btn" onClick={handleStartCustomScenario} disabled={scenarioBusy}>
                {scenarioBusy ? "生成中.." : "用我的场景开始"}
              </button>
            </div>
            {desc && <p>{desc}</p>}
          </div>
        </div>

        {/* 2) 시뮬레이션 */}
        <div className="mrdef ">
          <h3 className="sectionTitle">2. AI 员工应对模拟</h3>

          {/* 채팅 로그 */}
          <div className="chatbox">
            <div className="chatlog">
              {chat.map((m, i) => (
                <div key={i} className={`bubble ${m.role === "user" ? "from-user" : "from-patient"}`}>
                  <div className="text">{m.text}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 입력 */}
        <div className="chatInput ">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="请输入不满内容…"
          />
          <button className="btn" onClick={handleSend} disabled={busy}>
            {busy ? (
              <>
                发送中 <LoaderDot />
              </>
            ) : (
              "提出投诉"
            )}
          </button>
        </div>

        {/* 감정 분석 + 코칭 */}
        {emotionCard && (
          <div className="mrdef">
            <h4 className="sim-title">实时情绪分析与辅导</h4>
            <div>
              <div>
                <div>
                  <strong>识别到的情绪</strong>
                  <div>{emotionCard.emotion}</div>
                </div>
                <div className="border-l pl-4">
                  <strong>应对辅导</strong>
                  <div>{emotionCard.coachingTip}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 전략 설명 */}
        {strategyText && (
          <div className="mrdef">
            <h4 className="sim-title">AI 应对策略</h4>
            <div>{strategyText}</div>
          </div>
        )}

        {/* 액션 버튼들 */}
        <div className="mrdef">
          <div className="trfResponse_bottomBtn">
            <button className="btn" onClick={handlePredict} disabled={!hasAtLeastOneUserMsg}>
              AI 预测问题
            </button>
            <button className="btn" onClick={handleModelAnswer} disabled={!hasAtLeastOneUserMsg}>
              查看示范回答
            </button>
            <button className="btn" onClick={handleSummarize} disabled={!hasAtLeastOneUserMsg}>
              对话总结/评价
            </button>
            <button className="btn" onClick={handleReport} disabled={!hasAtLeastOneUserMsg}>
              生成应对报告
            </button>
            <button className="reset btn" onClick={handleReset}>
              全部重置
            </button>
          </div>
        </div>

        {/* 예상 질문 */}
        {predicted.length > 0 && (
          <div className="mrdef">
            <h4 className="sim-title">AI 的预测问题</h4>
            <div>
              <ul className="list-disc ">
                {predicted.map((q, i) => (
                  <li key={i}>{q}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* 모범 답변 */}
        {modelAns && (
          <div className="mrdef">
            <h4 className="sim-title">查看示范回答</h4>
            <div>
              <div>
                <strong>示范回答</strong>
              </div>
              <div>“{modelAns.modelResponse}”</div>
              <div>
                <strong>为什么有效</strong>
              </div>
              <div>{modelAns.rationale}</div>
            </div>
          </div>
        )}

        {/* 요약/평가 */}
        {summary && (
          <div className="mrdef">
            <h4 className="sim-title">对话总结与评价</h4>
            <div className=" ">
              <div>
                <strong>对话总结</strong>
                <p>{summary.summary}</p>
              </div>
              <div>
                <strong>应对评价</strong>
                <p>👍 优点: {summary.evaluation?.strength}</p>
                <p>💡 改进点: {summary.evaluation?.improvement}</p>
              </div>
              <div>
                <strong>推荐行动（Best Practice）</strong>
                <p>{summary.recommendation}</p>
              </div>
            </div>
          </div>
        )}

        {/* 보고서 */}
        {report && (
          <div className="mrdef">
            <h4 className="sim-title">顾客应对报告</h4>
            <div>
              {/* 언어별 키 처리(가이드에 따라) */}
              <div>
                <strong>처리일자/处理日期</strong> : {report["처리일자"] || report["处理日期"] || "-"}
              </div>
              <div>
                <strong>환자명/患者姓名</strong> : {report["환자명"] || report["患者姓名"] || "患者"}
              </div>
              <div>
                <strong>주요내용/主要内容</strong> : {report["주요내용"] || report["主要内容"] || "-"}
              </div>
              <div>
                <strong>진행된조치/采取措施</strong> : {report["진행된조치"] || report["采取措施"] || "-"}
              </div>
              <div>
                <strong>해결방안/解决方案</strong> : {report["해결방안"] || report["解决方案"] || "-"}
              </div>
              <div>
                <strong>향후조치필요사항/后续必要事项</strong> : {report["향후조치필요사항"] || report["后续必要事项"] || "-"}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
